/* ==========================================================================
   custom.js — JavaScript propio de AOK Labs
   --------------------------------------------------------------------------
   Todo el JS que no venga de Maestrooo vive aquí. theme.js es un bundle del
   vendor (esbuild) y se reemplaza entero en cada actualización de Stretch:
   nunca se edita.

   Se carga desde layout/theme.liquid con `defer`, así que se ejecuta después
   de parsear el HTML y antes de DOMContentLoaded.

   Stretch expone eventos propios (cart:change, carousel:settle,
   cart-drawer:refreshed, dialog:after-show…) sobre los que engancharse sin
   tocar el núcleo del tema.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Red de seguridad del cajón del carrito

   El tema repinta el cajón leyendo el HTML que Shopify devuelve junto al
   carrito:

     event.detail.cart["sections"][sectionId]        // theme.js, onCartChange

   Lo lee sin comprobar que venga. Shopify manda `sections: null` cuando la
   petición a /cart/change.js sale con el parámetro `sections` vacío, y entonces
   ese acceso lanza una excepción: el carrito SÍ cambia en el servidor, pero el
   cajón se queda con lo de antes y no se entera nada más del flujo. En AOK
   pasaba en producción con cada cambio de cantidad y con cada «quitar».

   Dos capas, las dos sin tocar theme.js:

   1. Se aporta el id de la sección del cajón al empaquetado. Si el tema ya lo
      añadió, el Set lo ignora por duplicado; si no llegó a añadirlo —que es la
      hipótesis— la petición ya sale con sección y el tema encuentra su HTML.

   2. Si aun así llega un `cart:change` sin secciones, se corta el evento en
      fase de captura sobre `document` —antes de que alcance al listener del
      tema— y se pide un `cart:refresh`, que es la otra vía que el propio tema
      tiene para repintarse: se baja la sección con ?section_id=… en vez de
      esperarla dentro de la respuesta del carrito. El usuario ve el cajón al
      día y no una excepción.
   -------------------------------------------------------------------------- */
(function () {
  function sectionIdDelCajon() {
    const drawer = document.querySelector('cart-drawer');
    const section = drawer?.closest('[id^="shopify-section-"]');

    return section ? section.id.replace('shopify-section-', '') : null;
  }

  document.addEventListener('cart:prepare-bundled-sections', function (event) {
    const id = sectionIdDelCajon();

    if (id && event.detail?.sections) event.detail.sections.add(id);
  });

  document.addEventListener(
    'cart:change',
    function (event) {
      const cart = event.detail?.cart;

      if (!cart || cart.sections != null) return;

      // Se para aquí: el listener del tema está en `document` en fase de burbuja
      // y con esto no llega a ejecutarse.
      event.stopPropagation();

      // Al añadir desde una ficha, el tema abre el cajón dentro del listener que
      // acabamos de cortar. Se replica su misma condición para no perder ese paso.
      const drawer = document.querySelector('cart-drawer');
      const abrirDespues =
        event.detail?.baseEvent === 'variant:add' &&
        window.themeVariables?.settings?.cartType === 'drawer' &&
        drawer &&
        !drawer.open;

      if (abrirDespues) {
        drawer.addEventListener('cart-drawer:refreshed', () => drawer.show?.(), { once: true });
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    },
    true
  );
})();


/* --------------------------------------------------------------------------
   Indicador de scroll del carrusel del footer en móvil
   Genera un punto por columna del footer y marca la que se está viendo.
   Los puntos se derivan de los bloques reales, así que siguen cuadrando si el
   cliente añade o quita columnas desde el editor de temas.
   -------------------------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', function () {
  const slider = document.querySelector('.footer__block-list--scrollable');
  const dots = document.querySelector('.footer-dots');

  if (!slider || !dots) return;

  const blocks = Array.from(slider.querySelectorAll('.footer__block'));

  if (blocks.length < 2) return;

  dots.replaceChildren(
    ...blocks.map(function (_, index) {
      const dot = document.createElement('span');
      dot.classList.toggle('active', index === 0);
      return dot;
    })
  );

  const marks = Array.from(dots.children);
  const origin = blocks[0].offsetLeft;

  function sync() {
    // El punto activo es el del bloque cuyo borde izquierdo queda más cerca del
    // inicio del área visible. Se calcula con la posición real de cada bloque:
    // miden 75vw, así que dividir el scroll por el ancho del contenedor
    // devolvía un índice equivocado en cuanto había más de dos columnas.
    let current = 0;
    let closest = Infinity;

    blocks.forEach(function (block, index) {
      const distance = Math.abs(block.offsetLeft - origin - slider.scrollLeft);

      if (distance < closest) {
        closest = distance;
        current = index;
      }
    });

    marks.forEach(function (mark, index) {
      mark.classList.toggle('active', index === current);
    });
  }

  slider.addEventListener('scroll', sync, { passive: true });
});


/* --------------------------------------------------------------------------
   Regalos por tramos
   -------------------------------------------------------------------------- */
(function () {
  const ENDPOINTS = { cart: '/cart.js', add: '/cart/add.js', change: '/cart/change.js' };

  function readRules() {
    const node = document.getElementById('aok-gift-rules');

    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  const rules = readRules();

  if (!rules) return;

  const tierByVariant = new Map();
  const blockingCodes = new Set(rules.blocking_codes || []);

  for (const rule of rules.tiers) {
    for (const variantId of rule.variants) tierByVariant.set(variantId, rule.tier);
  }

  let busy = false;

  function refreshTheme() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function showCorrectedCart() {
    if (rules.cart_page) {
      window.location.reload();
    } else {
      refreshTheme();
    }
  }

  async function readCart() {
    const response = await fetch(ENDPOINTS.cart, { headers: { Accept: 'application/json' } });

    if (!response.ok) throw new Error('No se pudo leer el carrito');

    return response.json();
  }

  function giftTierOf(item) {
    const byVariant = tierByVariant.get(item.variant_id);

    if (byVariant) return byVariant;

    const raw = item.properties && item.properties._aok_gift;

    return raw ? parseInt(String(raw), 10) : null;
  }

  function payableTotal(cart) {
    const lines = cart.items.reduce(
      (sum, item) => (giftTierOf(item) ? sum : sum + item.final_line_price),
      0
    );
    const cartDiscounts = (cart.cart_level_discount_applications || []).reduce(
      (sum, discount) => sum + (discount.total_allocated_amount || 0),
      0
    );

    return Math.max(0, lines - cartDiscounts);
  }

  function giftsAllowed(cart) {
    const blocked = (cart.discount_codes || []).some(
      (discount) => discount.applicable && blockingCodes.has(String(discount.code).trim().toUpperCase())
    );

    return Boolean(rules.enabled) && !blocked;
  }

  function isUnlocked(rule, total, allowed) {
    return Boolean(allowed && rule && rule.threshold !== null && total >= rule.threshold);
  }

  function corrections(cart) {
    const total = payableTotal(cart);
    const allowed = giftsAllowed(cart);
    const kept = new Set();
    const changes = [];

    for (const item of cart.items) {
      const tier = giftTierOf(item);

      if (!tier) continue;

      const rule = rules.tiers.find((t) => t.tier === tier);

      if (!isUnlocked(rule, total, allowed) || kept.has(tier)) {
        changes.push({ id: item.key, quantity: 0 });
        continue;
      }

      kept.add(tier);

      if (item.quantity > 1) changes.push({ id: item.key, quantity: 1 });
    }

    return changes;
  }

  async function changeLine(change) {
    const response = await fetch(ENDPOINTS.change, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(change)
    });

    if (!response.ok) throw new Error('No se pudo corregir el carrito');
  }

  async function applyCorrections(cart) {
    const changes = corrections(cart);

    for (const change of changes) await changeLine(change);

    return changes.length > 0;
  }

  async function addGift(variantId, tier) {
    await fetch(ENDPOINTS.add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _aok_gift: String(tier) } }]
      })
    });
  }

  async function chooseGift(variantId, tier) {
    if (busy) return;
    busy = true;

    try {
      const cart = await readCart();

      if (!giftsAllowed(cart)) return;

      for (const item of cart.items) {
        if (giftTierOf(item) === tier) await changeLine({ id: item.key, quantity: 0 });
      }

      await addGift(variantId, tier);
      refreshTheme();
    } catch (error) {
      console.warn('[AOK] No se pudo añadir el regalo:', error);
    } finally {
      busy = false;
    }
  }

  async function reconcile() {
    if (busy) return;
    busy = true;

    try {
      if (await applyCorrections(await readCart())) showCorrectedCart();
    } catch (error) {
      console.warn('[AOK] No se pudo revisar los regalos:', error);
    } finally {
      busy = false;
    }
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-aok-gift-variant]');

    if (!button) return;

    event.preventDefault();
    button.closest('x-modal')?.hide?.();
    chooseGift(Number(button.dataset.aokGiftVariant), Number(button.dataset.aokGiftTier));
  });

  let completando = false;

  function pendingGifts(cart) {
    const total = payableTotal(cart);
    const allowed = giftsAllowed(cart);
    const chosen = new Set(cart.items.map(giftTierOf).filter(Boolean));

    return rules.tiers.filter(
      (rule) => rule.first_variant && isUnlocked(rule, total, allowed) && !chosen.has(rule.tier)
    );
  }

  async function completarRegalos(event) {
    const boton = event.target.closest('[name="checkout"]');

    if (!boton || completando || busy) return;

    const formulario = boton.form || boton.closest('form');

    if (!formulario) return;

    event.preventDefault();
    completando = true;
    busy = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      let cart = await readCart();

      if (await applyCorrections(cart)) cart = await readCart();

      for (const rule of pendingGifts(cart)) await addGift(rule.first_variant, rule.tier);
    } catch (error) {
      console.warn('[AOK] No se pudieron revisar los regalos:', error);
    } finally {
      busy = false;
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      completando = false;
      formulario.requestSubmit(boton);
    }
  }

  document.addEventListener('click', completarRegalos, true);

  document.documentElement.addEventListener('cart:change', reconcile);
  document.addEventListener('cart-drawer:refreshed', reconcile);

  if (rules.review) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', reconcile);
    } else {
      reconcile();
    }
  }
})();
